import {FC, useState} from 'react'
import {useAuth} from '../core/Auth'

// On production me.photo is a signed URL that expires after an hour. An <img>
// that fails refetches `me` for a fresh one — at most once a minute, so a photo
// that is really broken falls back to initials instead of looping.
const REFRESH_INTERVAL_MS = 60_000
let lastRefreshAt = 0

const firstLetter = (name: string | undefined) => Array.from(name?.trim() ?? '')[0] ?? ''

type Props = {
  /** Side in px; must be a Metronic `symbol-{size}px` size. */
  size?: number
  /** Overrides me.photo: a preview URL, or null to force initials. */
  src?: string | null
}

/** The logged-in user's photo, or initials on a neutral background. */
const UserAvatar: FC<Props> = ({size = 50, src}) => {
  const {currentUser, refreshUser} = useAuth()
  const [failedSrc, setFailedSrc] = useState<string>()
  const photo = src === undefined ? currentUser?.pic : src
  const initials = `${firstLetter(currentUser?.first_name)}${firstLetter(
    currentUser?.last_name
  )}`.toUpperCase()

  const handleError = () => {
    setFailedSrc(photo ?? undefined)
    if (src === undefined && Date.now() - lastRefreshAt > REFRESH_INTERVAL_MS) {
      lastRefreshAt = Date.now()
      refreshUser()
    }
  }

  return (
    <div className={`symbol symbol-${size}px`}>
      {photo && photo !== failedSrc ? (
        <img src={photo} alt='' style={{objectFit: 'cover'}} onError={handleError} />
      ) : (
        <span
          className={`symbol-label bg-secondary text-gray-700 fw-bold ${
            size >= 100 ? 'fs-2hx' : 'fs-4'
          }`}
        >
          {initials || '—'}
        </span>
      )}
    </div>
  )
}

export {UserAvatar}
